import React from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import type { UISuggestion } from '../../types/grammar';

export type CorrectionsBottomSheetProps = {
  open: boolean;
  onClose: () => void;
  suggestions: UISuggestion[];
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onAccept: (suggestion: UISuggestion) => void;
  onSkip: () => void;
};

export const CorrectionsBottomSheet: React.FC<CorrectionsBottomSheetProps> = ({
  open,
  onClose,
  suggestions,
  activeIndex,
  onPrev,
  onNext,
  onAccept,
  onSkip,
}) => {
  if (!open) return null;

  const total = suggestions.length;
  const current = suggestions[activeIndex];
  const isFirst = activeIndex <= 0;
  const isLast = activeIndex >= total - 1;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 sm:hidden"
        onClick={onClose}
        aria-hidden
      />
      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 max-h-[55vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shadow-2xl sm:hidden"
        aria-modal
        aria-label="Grammar corrections"
      >
        {/* Header: Grammar N of M + prev/next + close */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Grammar {Math.min(activeIndex + 1, total)} of {total}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onPrev}
              disabled={isFirst}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none text-slate-600 dark:text-slate-400"
              aria-label="Previous correction"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={isLast}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none text-slate-600 dark:text-slate-400"
              aria-label="Next correction"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content: mistake → correction chips + reason */}
        <div className="px-4 py-4 space-y-4">
          {current ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex px-3 py-1.5 rounded-xl bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 text-sm font-medium border border-red-200 dark:border-red-800">
                  {current.original}
                </span>
                <span className="text-slate-400 dark:text-slate-500" aria-hidden>→</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 text-sm font-medium border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  {current.suggestion}
                </span>
              </div>
              {current.reason_tamil && (
                <p className="text-xs tamil-text text-slate-600 dark:text-slate-400 leading-relaxed">
                  {current.reason_tamil}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">No correction selected.</p>
          )}
        </div>

        {/* Actions: Skip / Accept */}
        <div className="flex gap-3 px-4 pb-4 pt-2 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onSkip}
            className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() => current && onAccept(current)}
            disabled={!current}
            className="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm disabled:opacity-50 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </>
  );
};

export default CorrectionsBottomSheet;
